/**
 * KBC Quiz Application Wrapper
 * Integrates quiz game with MSAL authentication and backend API
 * 
 * TEMPORARY - FOR EVENT ONLY
 * This file can be safely deleted when removing the quiz feature
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal, useAccount } from '@azure/msal-react';
import KBCLandingPage from './KBCLandingPage';
import RulesScreen from './RulesScreen';
import GameIntro from './GameIntro';
import QuestionCard from './QuestionCard';
import RiskModal from './RiskModal';
import QuitModal from './QuitModal';
import EndScreen from './EndScreen';
import Leaderboard from './Leaderboard';
import { GameScreen, QuizQuestion, UserAnswer, PrizeTier, LeaderboardEntry, AnswerFeedback } from './types';
import { getQuizQuestions } from './data/questions';
import { getPrizeForLevel, isPrizeTierEnabled, getTierFromCorrectAnswers, getPrizeForCorrectAnswers, fetchPrizeTiers } from './config/prizeConfig';
import { useAudio } from './hooks/useAudio';
import AdminPanel from './AdminPanel';
import {
  startQuizAttempt,
  saveQuizProgress,
  completeQuizAttempt,
  getLeaderboard,
  getUserAttempt,
  getAttemptsRemaining,
  LeaderboardEntry as ApiLeaderboardEntry,
} from '../../services/quizApi';

// Admin emails list (current admin first)
const ADMIN_EMAILS: string[] = [
    'paymanshu.sharma@maersk.com',
    'shantanu.rastogi@maersk.com',
    'srikant.yadav1@maersk.com',
    'krisha.joshi@maersk.com',
    'nikhil.nagesh@maersk.com'
];

// LocalStorage key for quiz state persistence
const STORAGE_KEY = 'kbc_quiz_state';

interface PersistedQuizState {
  screen: GameScreen;
  hasAttempted: boolean;
  attemptId?: string;
  questions: QuizQuestion[];
  currentQuestionIndex: number;
  userAnswers: UserAnswer[];
  score: number;
  currentLevel: number;
  quizStartTime: number;
  cashedOut: boolean;
  walkedOut: boolean;
  finalPrize?: PrizeTier;
}

function KBCQuizApp() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  
  // Get user info from MSAL
  const userEmail = account?.username || '';
  const displayName = account?.name || 'Guest User';
  const isAdmin = userEmail ? ADMIN_EMAILS.includes(userEmail) : false;

  // Core state
  const [screen, setScreen] = useState<GameScreen>('landing');
  const [hasAttempted, setHasAttempted] = useState(false);
  const [attemptId, setAttemptId] = useState<string | undefined>(undefined);
  const [showIntro, setShowIntro] = useState(false);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<UserAnswer[]>([]);
  const [score, setScore] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [feedback, setFeedback] = useState<AnswerFeedback>('none');
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [cashedOut, setCashedOut] = useState(false);
  const [walkedOut, setWalkedOut] = useState(false);
  const [finalPrize, setFinalPrize] = useState<PrizeTier | undefined>(undefined);
  const [quizStartTime, setQuizStartTime] = useState<number>(0);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoadingState, setIsLoadingState] = useState(true);
  const [previousAttemptData, setPreviousAttemptData] = useState<any>(null); // Store previous attempt for viewing

  // Audio hook
  const { audioEnabled, playSound, toggleAudio } = useAudio();

  // Fetch prize config on mount
  useEffect(() => {
    fetchPrizeTiers().catch(error => {
      console.error('Failed to load prize config:', error);
    });
  }, []);

    // Load persisted state on mount
  useEffect(() => {
    const loadPersistedState = async () => {
      try {
        // DATABASE is the SOURCE OF TRUTH - check backend first
        if (userEmail) {
          const existingAttempt = await getUserAttempt(userEmail);
          
          if (existingAttempt && !existingAttempt.is_completed) {
            // User has an incomplete attempt in database
            console.log('Database: Found incomplete attempt', existingAttempt.id);
            
            // Try to restore from localStorage if available (for UI state continuity)
            const savedState = localStorage.getItem(STORAGE_KEY);
            if (savedState) {
              try {
                const parsed: PersistedQuizState = JSON.parse(savedState);
                
                // Verify localStorage matches database attempt
                if (parsed.attemptId === existingAttempt.id) {
                  // Restore full state from localStorage
                  setScreen(parsed.screen);
                  setHasAttempted(parsed.hasAttempted);
                  setAttemptId(parsed.attemptId);
                  setQuestions(parsed.questions);
                  setCurrentQuestionIndex(parsed.currentQuestionIndex);
                  setUserAnswers(parsed.userAnswers);
                  setScore(parsed.score);
                  setCurrentLevel(parsed.currentLevel);
                  setQuizStartTime(parsed.quizStartTime);
                  setCashedOut(parsed.cashedOut);
                  setWalkedOut(parsed.walkedOut);
                  setFinalPrize(parsed.finalPrize);
                  
                  console.log('Quiz state restored from localStorage (matches database)');
                } else {
                  // localStorage is stale - clear it and use database
                  console.log('LocalStorage stale, clearing...');
                  localStorage.removeItem(STORAGE_KEY);
                  setHasAttempted(true);
                  setAttemptId(existingAttempt.id);
                }
              } catch (parseError) {
                console.error('Error parsing localStorage, clearing:', parseError);
                localStorage.removeItem(STORAGE_KEY);
                setHasAttempted(true);
                setAttemptId(existingAttempt.id);
              }
            } else {
              // No localStorage - just mark as attempted from database
              console.log('No localStorage found, using database state only');
              setHasAttempted(true);
              setAttemptId(existingAttempt.id);
            }
          } else if (existingAttempt && existingAttempt.is_completed) {
            // User has completed an attempt - check if they have remaining attempts
            console.log('Database: User has completed an attempt, checking remaining attempts');
            setPreviousAttemptData(existingAttempt); // Store for viewing results
            
            // Check remaining attempts
            try {
              const attemptsInfo = await getAttemptsRemaining(userEmail);
              console.log('Attempts remaining:', attemptsInfo.remaining_attempts);
              setHasAttempted(!attemptsInfo.can_attempt); // Block if no attempts left
            } catch (error) {
              console.error('Error checking attempts remaining:', error);
              setHasAttempted(false); // On error, allow them to try (backend will validate)
            }
            
            // Clear any stale localStorage
            localStorage.removeItem(STORAGE_KEY);
          } else {
            // No attempt in database - user can play
            console.log('Database: No attempt found, user can play');
            setHasAttempted(false);
            // Clear any stale localStorage
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error('Error loading persisted state:', error);
        // On error, check localStorage as fallback
        const savedState = localStorage.getItem(STORAGE_KEY);
        if (savedState) {
          console.log('Database error, falling back to localStorage');
          try {
            const parsed: PersistedQuizState = JSON.parse(savedState);
            setHasAttempted(parsed.hasAttempted);
          } catch (parseError) {
            console.error('Error parsing localStorage fallback:', parseError);
          }
        }
      } finally {
        setIsLoadingState(false);
      }
    };

    loadPersistedState();
  }, [userEmail]);

  // Load leaderboard on mount and when viewing leaderboard screen
  useEffect(() => {
    const loadLeaderboard = async () => {
      try {
        const apiLeaderboard = await getLeaderboard(50, true); // Include incomplete attempts
        
        // Convert API format to component format
        const formattedLeaderboard: LeaderboardEntry[] = apiLeaderboard.map((entry: ApiLeaderboardEntry) => ({
          rank: entry.rank,
          userId: entry.user_email,
          displayName: entry.display_name,
          questionsCorrect: entry.correct_answers,
          time: entry.time_taken_seconds,
          prizeLevel: entry.prize_level,
          prizeWon: entry.prize_won,
          status: entry.status,
          isCompleted: entry.is_completed,
          walkedOut: entry.walked_out,
          cashedOut: entry.cashed_out,
        }));
        
        setLeaderboard(formattedLeaderboard);
      } catch (error) {
        console.error('Error loading leaderboard:', error);
      }
    };

    if (screen === 'leaderboard' || screen === 'end') {
      loadLeaderboard();
    }
  }, [screen]);

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    if (!isLoadingState && hasAttempted && screen !== 'landing') {
      const stateToSave: PersistedQuizState = {
        screen,
        hasAttempted,
        attemptId,
        questions,
        currentQuestionIndex,
        userAnswers,
        score,
        currentLevel,
        quizStartTime,
        cashedOut,
        walkedOut,
        finalPrize,
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    }
  }, [
    screen,
    hasAttempted,
    attemptId,
    questions,
    currentQuestionIndex,
    userAnswers,
    score,
    currentLevel,
    quizStartTime,
    cashedOut,
    walkedOut,
    finalPrize,
    isLoadingState,
  ]);

  // Clear localStorage when quiz is completed or user returns to landing
  useEffect(() => {
    if (screen === 'landing') {
      // Always clear localStorage when returning to landing
      // This ensures admin deletions take effect immediately
      const hadData = localStorage.getItem(STORAGE_KEY);
      if (hadData) {
        console.log('Clearing localStorage on return to landing');
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [screen]);

  // Initialize questions and start quiz
  const startQuiz = async () => {
    if (hasAttempted) return; // prevent multiple attempts
    
    try {
      console.log('🎮 Starting quiz for user:', userEmail);
      
      // Get 20 questions: 4 from each of 5 difficulty tiers
      // RANDOMIZATION: Each tier's questions are shuffled independently
      // Ensures every quiz attempt has a unique question set
      const quizQuestions = getQuizQuestions(4, 5);
      setQuestions(quizQuestions);
      setCurrentQuestionIndex(0);
      setUserAnswers([]);
      setScore(0);
      setCurrentLevel(0);
      setFeedback('none');
      setShowRiskModal(false);
      setShowQuitModal(false);
      setCashedOut(false);
      setWalkedOut(false);
      setFinalPrize(undefined);
      setQuizStartTime(Date.now());
      
      // Start attempt in backend
      console.log('📡 Calling startQuizAttempt API...');
      const response = await startQuizAttempt(userEmail, displayName, quizQuestions.length);
      console.log('📥 Received response:', response);
      
      // Backend returns {status, attempt_id, message, attempt}
      // Status can be: 'new', 'existing', or 'completed'
      if (response.status === 'completed') {
        // User has exhausted all attempts
        console.log('⛔ All attempts exhausted:', response.message);
        setHasAttempted(true);
        alert(response.message || 'You have used all available attempts.');
        setScreen('landing');
        return;
      }
      
      const attemptIdValue = response.attempt_id || response.attempt.id;
      
      console.log('🆔 Setting attemptId to:', attemptIdValue);
      setAttemptId(attemptIdValue);
      
      if (!attemptIdValue) {
        throw new Error('No attempt ID received from backend!');
      }
      
      // Show intro sequence for first question (6 seconds)
      setScreen('quiz');
      setShowIntro(true);
      
      console.log('✅ Quiz started successfully with attempt ID:', attemptIdValue);
    } catch (error) {
      console.error('❌ Error starting quiz:', error);
      alert('Failed to start quiz. Please try again.');
    }
  };

  // Handle intro completion
  const handleIntroComplete = () => {
    setShowIntro(false);
  };

  // Handle quit button click
  const handleQuitClick = () => {
    setShowQuitModal(true);
  };

  // Handle confirmed walk-out
  const handleConfirmQuit = async () => {
    const correctCount = userAnswers.filter(a => a.correct).length;
    setWalkedOut(true);
    setShowQuitModal(false);
    playSound('wrong');
    
    const elapsedSeconds = Math.floor((Date.now() - quizStartTime) / 1000);
    
    // Complete quiz in backend with walk-out
    if (attemptId) {
      try {
        console.log(`🚪 Walking out: ${correctCount} correct answers`);
        await completeQuizAttempt(
          attemptId,
          correctCount,
          undefined,
          undefined,
          false,
          true,
          elapsedSeconds
        );
      } catch (error) {
        console.error('Error completing quiz (walk-out):', error);
      }
    }
    
    setFinalPrize(undefined);
    setScreen('end');
  };

  // Handle cancel quit
  const handleCancelQuit = () => {
    setShowQuitModal(false);
  };

  // Handle answer submission
  const handleAnswer = async (optionId: string) => {
    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = optionId === currentQuestion.correctOption;
    const questionStartTime = Date.now();

    // Record answer
    const answer: UserAnswer = {
      questionId: currentQuestion.id,
      selectedOption: optionId,
      correct: isCorrect,
      timestamp: new Date(),
    };
    setUserAnswers([...userAnswers, answer]);

    // Show feedback
    setFeedback(isCorrect ? 'correct' : 'wrong');
    playSound(isCorrect ? 'correct' : 'wrong');

    // Save progress to backend
    if (attemptId) {
      try {
        const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);
        console.log(`💾 Saving progress: Q${currentQuestionIndex + 1}, correct=${isCorrect}, time=${timeTaken}s`);
        await saveQuizProgress(
          attemptId,
          currentQuestionIndex,
          currentQuestion.id,
          optionId,
          isCorrect,
          timeTaken
        );
        console.log(`✓ Progress saved successfully`);
      } catch (error) {
        console.error('❌ Error saving progress:', error);
        // Continue even if backend save fails
      }
    } else {
      console.warn('⚠️  No attemptId - cannot save progress!');
    }

    if (isCorrect) {
      const correctCount = userAnswers.filter(a => a.correct).length + 1;
      const currentTier = getTierFromCorrectAnswers(correctCount);
      const previousTier = getTierFromCorrectAnswers(correctCount - 1);
      const justCompletedTier = currentTier > previousTier && currentTier > 0;
      
      setCurrentLevel(currentTier);

      if (justCompletedTier && isPrizeTierEnabled(currentTier)) {
        setShowRiskModal(true);
      }
    }
  };

  // Handle next question button
  const handleNextQuestion = () => {
    if (feedback === 'wrong') {
      endGame(false);
    } else if (feedback === 'correct') {
      moveToNextQuestion();
    }
  };

  // Move to next question
  const moveToNextQuestion = () => {
    setFeedback('none');
    setShowRiskModal(false);

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      playSound('questionSting');
    } else {
      endGame(true);
    }
  };

  // Handle risk it
  const handleRiskIt = () => {
    setShowRiskModal(false);
    moveToNextQuestion();
  };

  // Handle cash out
  const handleCashOut = async () => {
    const prize = getPrizeForLevel(currentLevel);
    const correctCount = userAnswers.filter(a => a.correct).length;
    setFinalPrize(prize);
    setCashedOut(true);
    setShowRiskModal(false);
    playSound('cashout');
    
    const elapsedSeconds = Math.floor((Date.now() - quizStartTime) / 1000);
    
    // Complete quiz in backend with cash-out
    if (attemptId && prize) {
      try {
        console.log(`💰 Cashing out: ${correctCount} correct answers, prize: ${prize.label}`);
        await completeQuizAttempt(
          attemptId,
          correctCount,
          prize.label,
          prize.level,
          true,
          false,
          elapsedSeconds
        );
      } catch (error) {
        console.error('Error completing quiz (cash-out):', error);
      }
    }
    
    setTimeout(() => {
      endGame(true);
    }, 1000);
  };

  // End game
  const endGame = async (completed: boolean) => {
    const elapsedSeconds = Math.floor((Date.now() - quizStartTime) / 1000);
    const correctCount = userAnswers.filter(a => a.correct).length;

    // Determine final prize
    if (!cashedOut && completed) {
      const prize = getPrizeForCorrectAnswers(correctCount);
      setFinalPrize(prize);
      playSound('victory');
      
      // Complete quiz in backend (full completion)
      if (attemptId) {
        try {
          console.log(`🏆 Completing quiz: ${correctCount} correct answers`);
          await completeQuizAttempt(
            attemptId,
            correctCount,
            prize?.label,
            prize?.level,
            false,
            false,
            elapsedSeconds
          );
        } catch (error) {
          console.error('Error completing quiz:', error);
        }
      }
    } else if (!cashedOut && !walkedOut) {
      // Wrong answer scenario - complete without prize
      playSound('wrong');
      
      if (attemptId) {
        try {
          console.log(`❌ Completing quiz (wrong answer): ${correctCount} correct answers`);
          await completeQuizAttempt(
            attemptId,
            correctCount,
            undefined,
            0,
            false,
            false,
            elapsedSeconds
          );
        } catch (error) {
          console.error('Error completing quiz (wrong answer):', error);
        }
      }
    } else if (cashedOut || walkedOut) {
      // Already completed via cash-out or walk-out - don't complete again
      console.log(`✓ Quiz already completed (cashedOut=${cashedOut}, walkedOut=${walkedOut})`);
    }

    setScreen('end');
    
    // Reload leaderboard to show updated rankings
    try {
      const apiLeaderboard = await getLeaderboard(50, true); // Include incomplete attempts
      const formattedLeaderboard: LeaderboardEntry[] = apiLeaderboard.map((entry: ApiLeaderboardEntry) => ({
        rank: entry.rank,
        userId: entry.user_email,
        displayName: entry.display_name,
        questionsCorrect: entry.correct_answers,
        time: entry.time_taken_seconds,
        prizeLevel: entry.prize_level,
        prizeWon: entry.prize_won,
        status: entry.status,
        isCompleted: entry.is_completed,
        walkedOut: entry.walked_out,
        cashedOut: entry.cashed_out,
      }));
      setLeaderboard(formattedLeaderboard);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  };

  // View leaderboard
  const handleViewLeaderboard = () => {
    setScreen('leaderboard');
  };

  // Back from leaderboard
  const handleBackFromLeaderboard = () => {
    setScreen(userAnswers.length > 0 ? 'end' : 'landing');
  };

  // Return to main app
  const handleReturnToMainApp = () => {
    localStorage.removeItem(STORAGE_KEY);
    navigate('/');
  };

  // Show loading state while checking for existing attempts
  if (isLoadingState) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-kbc-dark via-gray-900 to-black flex items-center justify-center">
        <div className="text-kbc-gold text-2xl font-bold animate-pulse">Loading Quiz...</div>
      </div>
    );
  }

  // Handle view admin
  const handleViewAdmin = () => {
    setScreen('admin');
  };

  // Handle view my result (for users who already completed)
  const handleViewMyResult = async () => {
    if (!previousAttemptData && userEmail) {
      // Fetch attempt data if not already loaded
      try {
        const attempt = await getUserAttempt(userEmail);
        if (attempt) {
          setPreviousAttemptData(attempt);
          // Set up state for EndScreen display
          setScore(attempt.final_score || 0);
          setCashedOut(attempt.cashed_out);
          setWalkedOut(attempt.walked_out);
          
          // Set prize if available
          if (attempt.prize_level && attempt.prize_won) {
            setFinalPrize({
              level: attempt.prize_level,
              label: attempt.prize_won,
              value: 0,
              enabled: true
            });
          }
          
          // Generate mock questions array for totalQuestions prop
          const mockQuestions = Array(attempt.total_questions).fill({});
          setQuestions(mockQuestions as QuizQuestion[]);
          
          // Generate mock user answers for correctAnswers count
          const mockAnswers = Array(attempt.correct_answers).fill({ correct: true });
          setUserAnswers(mockAnswers as UserAnswer[]);
        }
      } catch (error) {
        console.error('Error loading previous attempt:', error);
      }
    }
    
    setScreen('end');
  };

  // Render current screen
  const renderScreen = () => {
    if (showIntro) {
      return <GameIntro onComplete={handleIntroComplete} />;
    }

    switch (screen) {
      case 'landing':
        return (
          <div className="relative">
            <KBCLandingPage
              onStart={() => !hasAttempted && setScreen('rules')}
              onViewRules={() => setScreen('rules')}
              onViewLeaderboard={() => setScreen('leaderboard')}
              onViewAdmin={isAdmin ? handleViewAdmin : undefined}
              onViewMyResult={hasAttempted ? handleViewMyResult : undefined}
              hasAttempted={hasAttempted}
              isAdmin={isAdmin}
            />
            {/* Return to Main App Button */}
            <button
              onClick={handleReturnToMainApp}
              className="absolute top-4 right-4 px-3 py-1.5 sm:px-6 sm:py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200 font-medium text-sm sm:text-base"
            >
              <span className="hidden sm:inline">← Back to Home Page</span>
              <span className="sm:hidden">← Home</span>
            </button>
          </div>
        );

      case 'rules':
        return (
          <RulesScreen
            onStart={startQuiz}
            onBack={() => setScreen('landing')}
            audioEnabled={audioEnabled}
            onToggleAudio={toggleAudio}
            hasAttempted={hasAttempted}
          />
        );

      case 'quiz':
        if (questions.length === 0) return null;
        return (
          <>
            <QuestionCard
              question={questions[currentQuestionIndex]}
              questionNumber={currentQuestionIndex + 1}
              totalQuestions={questions.length}
              onAnswer={handleAnswer}
              onNextQuestion={handleNextQuestion}
              onQuitClick={handleQuitClick}
              feedback={feedback}
              correctOption={questions[currentQuestionIndex].correctOption}
              disabled={feedback !== 'none'}
              audioEnabled={audioEnabled}
              onToggleAudio={toggleAudio}
            />
            {showRiskModal && (
              <RiskModal
                currentPrize={getPrizeForLevel(currentLevel)!}
                nextPrize={getPrizeForLevel(currentLevel + 1)}
                onRiskIt={handleRiskIt}
                onCashOut={handleCashOut}
              />
            )}
            {showQuitModal && (
              <QuitModal
                onConfirmQuit={handleConfirmQuit}
                onCancel={handleCancelQuit}
              />
            )}
          </>
        );

      case 'end':
        return (
          <div className="relative">
            <EndScreen
              score={score}
              prize={finalPrize}
              totalQuestions={questions.length}
              correctAnswers={userAnswers.filter(a => a.correct).length}
              cashedOut={cashedOut}
              walkedOut={walkedOut}
              leaderboardPreview={leaderboard.slice(0, 5)}
              onViewFullLeaderboard={handleViewLeaderboard}
              onBackToHome={() => setScreen('landing')}
            />
            {/* Return to Main App Button */}
            <button
              onClick={handleReturnToMainApp}
              className="absolute top-4 right-4 px-3 py-1.5 sm:px-6 sm:py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200 font-medium text-sm sm:text-base z-50"
            >
              <span className="hidden sm:inline">← Back to Home Page</span>
              <span className="sm:hidden">← Home</span>
            </button>
          </div>
        );

      case 'leaderboard':
        return (
          <div className="relative">
            <Leaderboard
              entries={leaderboard}
              onBack={handleBackFromLeaderboard}
              isLive={true}
            />
            {/* Return to Main App Button */}
            <button
              onClick={handleReturnToMainApp}
              className="absolute top-4 right-4 px-3 py-1.5 sm:px-6 sm:py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200 font-medium text-sm sm:text-base z-50"
            >
              <span className="hidden sm:inline">← Back to Home Page</span>
              <span className="sm:hidden">← Home</span>
            </button>
          </div>
        );

      case 'admin':
        if (!isAdmin) {
          // Security: redirect non-admins back to landing
          setScreen('landing');
          return null;
        }
        return (
          <AdminPanel
            adminEmail={userEmail}
            onBack={() => setScreen('landing')}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="KBCQuizApp">
      {renderScreen()}
    </div>
  );
}

export default KBCQuizApp;
